import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MarketingTags from '@/components/MarketingTags';
import { getGoogleFlightsBookingOptions, fareOptionsFailureMessage } from '../api/functions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SectionBand from '@/components/ui/SectionBand';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { cn } from '@/lib/utils';
import {
  REVIEW_SECTION_CARD,
  BRAND_NESTED_PANEL,
  BRAND_OPTION_CARD_SELECTED,
  BRAND_OPTION_CARD_UNSELECTED,
  BRAND_PRIMARY_BTN_CLASS,
} from '@/constants/brandColors';
import { Loader2, Check, Plane, Luggage, Info, RotateCcw, ShieldCheck, CheckCircle, Zap, TrendingUp, Minus, Clock, Sparkles } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import FlightOfferCard from '../components/search/FlightOfferCard';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateInstallmentPlan } from '../utils/installmentCalculator';
import { getDefaultInstallmentDepositPercentForLeadDays, SLICE_PAY_HOLD_FEE_RATE, DEFERA_CAPITAL_HOLD_FEE_RATE } from '../utils/paymentScheduleCalculator';
import { formatDisplayPrice, calculateDisplayPrice, dollarsToMiles, milesToDollars, createPageUrl, getServiceFeeRate, withoutRetiredMilesMethod } from '../utils';
import { useBookingFlow } from '../context/BookingFlowContext';
import AllInPriceToggle from '@/components/ui/AllInPriceToggle';
import { useAllInPrices } from '@/hooks/useAllInPrices';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { withInferredFareNames } from '../utils/marketingFareName';
import { withDepositTwin, withDepositTwins } from '@/utils/checkoutPaymentMethod';
import { trackEvent } from '@/lib/activityTracker';
import {
  checkFareOptionIntegrity,
  fareForStamp,
  slicesFromSelectedFlights,
} from '../../supabase/functions/_shared/fare-option-integrity.js';

/** Provenance of a booking token, for telemetry (mirrors the server's
 *  source-routed dispatch prefixes). */
const bookingTokenKind = (t) => !t ? 'none'
  : t.startsWith('duffel:') ? 'duffel'
  : t.startsWith('searchapi:') ? 'searchapi'
  : t.startsWith('mondee:') ? 'mondee' : 'serpapi';

export default function FlightOptions() {
  // Carries the search page's price-display choice through booking options.
  const [showAllInPrices, setShowAllInPrices] = useAllInPrices();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateBookingState, startNewSession, selectedFlight: contextFlight, returnFlight: contextReturn, searchParams: contextSearch, paymentMethod: contextPayment } = useBookingFlow();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  // Always show loading state until API data is ready
  const [isLoading, setIsLoading] = useState(true);
  const [bookingOptions, setBookingOptions] = useState([]);
  // Which bookingToken already emitted its booking_options_result event —
  // one event per options load, whether it ends in cards, an empty card, or
  // a fetch error.
  const optionsResultTrackedRef = useRef(null);
  const [selectedFlights, setSelectedFlights] = useState([]);
  const [baggagePrices, setBaggagePrices] = useState({});
  const [priceInsights, setPriceInsights] = useState(null);
  const [priceForecast, setPriceForecast] = useState(null);
  const [priceForecastLoading, setPriceForecastLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  
  // Get payment method from location state or context (fallback for refresh/auth return).
  // A retired Miles method (old link or cart) reads as installments, as on
  // Review: Miles are applied at checkout now, never priced here at 1¢.
  const paymentMethod = withoutRetiredMilesMethod(location.state?.paymentMethod || contextPayment || 'installments');
  // When router state exists, use only that for search metadata — never mix in stale context from abandoned_trips.
  const isChangeFlight = (location.state != null ? location.state.searchParams : contextSearch)?.search_method === 'change_flight';
  const changeFlightTrip = location.state?.changeFlightTrip || null;
  const isFlightStayPackage =
    location.state?.packageType === 'flight_stay' || location.state?.tripMode === 'flight_stay';
  const deferaZero = !isChangeFlight && location.state?.deferaZero === true && !isFlightStayPackage;
  const deferaZeroToggle =
    !isChangeFlight && location.state?.deferaZeroToggle === true && !isFlightStayPackage;
  const milesBalance = location.state?.milesBalance || 0;
  const [reserveBalanceCents, setReserveBalanceCents] = useState(location.state?.reserveBalanceCents ?? 0);
  const reserveTripId = location.state?.reserveTripId || null;

  // Fetch reserve balance when payment is reserve (e.g. from Book Flight flow where it wasn't passed)
  useEffect(() => {
    if (paymentMethod !== 'reserve') return;
    const fetchBalance = async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { reserveUrl } = await import('@/lib/apiUrls');
        const { mergeReserveActAsHeaders } = await import('@/lib/reserveActAs');
        const res = await fetch(reserveUrl('balance'), {
          headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
        });
        if (res.ok) {
          const data = await res.json();
          setReserveBalanceCents(data.balance_cents ?? 0);
        }
      } catch (e) {
        /* keep existing value */
      }
    };
    fetchBalance();
  }, [paymentMethod]);

  // Flight data for display (slices format, same as Review page).
  // If `location.state` is present, trust only navigation — do not fall back to contextFlight (can be stale from DB).
  const nav = location.state;
  const selectedFlight = nav != null ? nav.selectedFlight ?? null : contextFlight;
  const searchParams = nav != null ? nav.searchParams ?? null : contextSearch;
  const explicitReturn = nav && 'returnFlight' in nav ? nav.returnFlight : undefined;
  // If navigation included a return leg (Reserve switch / round trip), always merge it — do not infer one-way from
  // searchParams.slices.length alone (that array can drop the return slice while still being a round trip).
  const hasReturnLegFromNav =
    explicitReturn != null &&
    Array.isArray(explicitReturn.slices) &&
    explicitReturn.slices.length > 0;
  const isOneWayTrip = !hasReturnLegFromNav && searchParams?.slices?.length === 1;
  const returnFlight = hasReturnLegFromNav
    ? explicitReturn
    : isOneWayTrip
      ? null
      : nav != null && 'returnFlight' in nav
        ? nav.returnFlight
        : nav == null
          ? contextReturn
          : null;
  /** True when we navigated from Defera Flights with a concrete offer — never show API `selectedFlights` dates for that (they can lag the chosen itinerary). */
  const cameFromSearchWithOffer = !!location.state?.selectedFlight;

  // Align context with this navigation so nothing else reads stale itinerary
  // from rehydrated abandoned_trips. In a NEW booking session: this used to
  // write the opened flight into the previous session's cart, which kept its
  // old fare (29% of carts held a fare for other flights) and had its
  // last_activity bumped by a flight the customer merely looked at.
  const syncedBookingTokenRef = useRef(null);
  useEffect(() => {
    const token = nav?.bookingToken;
    const sf = nav?.selectedFlight;
    if (!token || !sf) return;
    if (syncedBookingTokenRef.current === token) return;
    syncedBookingTokenRef.current = token;
    startNewSession();
    updateBookingState({
      selectedFlight: sf,
      returnFlight: nav && 'returnFlight' in nav ? nav.returnFlight : null,
      searchParams: nav.searchParams ?? null,
      bookingOption: null,
      choseRefundableInstallments: false,
      ...(typeof nav.paymentMethod === 'string' ? { paymentMethod: withoutRetiredMilesMethod(nav.paymentMethod) } : {})
    });
  }, [nav, updateBookingState, startNewSession]);

  const flightForDisplay = useMemo(() => {
    if (!selectedFlight) return null;
    const outSlices = selectedFlight?.slices || [];
    const retSlices = returnFlight?.slices || [];
    if (retSlices.length === 0) return selectedFlight;
    return { ...selectedFlight, slices: [...outSlices, ...retSlices] };
  }, [selectedFlight, returnFlight]);

  /** The fare picked here, stamped with the flights and booking token it was
   *  priced for, so Review and checkout can prove it belongs to these flights
   *  (supabase/functions/_shared/fare-option-integrity.js). */
  const pickedBookingToken = location.state?.bookingToken || null;
  const stampFare = useCallback((option) => {
    if (!option) return option;
    const slices = flightForDisplay?.slices?.length ? flightForDisplay.slices : null;
    if (!slices) return option;
    return { ...option, _fare_for: fareForStamp(slices, pickedBookingToken) };
  }, [flightForDisplay, pickedBookingToken]);

  // Defera Zero requires departure at least 2 months from today
  const departureDateEligibleForDeferaZero = useMemo(() => {
    let depDate = null;
    const firstSeg = selectedFlight?.slices?.[0]?.segments?.[0];
    if (firstSeg?.departing_at) {
      depDate = new Date(firstSeg.departing_at);
    } else if (selectedFlights?.length > 0 && selectedFlights[0]?.flights?.[0]?.departure_airport?.time) {
      depDate = new Date(selectedFlights[0].flights[0].departure_airport.time);
    }
    if (!depDate || isNaN(depDate.getTime())) return true; // Unknown date: show DZ
    const now = new Date();
    const minDeparture = new Date(now);
    minDeparture.setMonth(minDeparture.getMonth() + 2);
    return depDate > minDeparture;
  }, [selectedFlight, selectedFlights]);

  const changeFlightOriginalBaseCents = useMemo(() => {
    if (!changeFlightTrip?.length) return 0;
    const main = changeFlightTrip[0];
    const fd = main?.flightData || main?.flight_data || {};
    const ps = fd?.pricing_snapshot;
    const paymentMethod = fd?._payment_method || fd?.payment_method || main?.payment_method || 'installments';
    const svc = parseFloat(ps?.service_fee || main?.service_fee || fd?.service_fee || 0);
    const hold = parseFloat(ps?.hold_fee || main?.hold_fee || fd?.hold_fee || 0);
    const gt = parseFloat(ps?.grand_total || main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
    // Admin-set actual cost overrides all other base fare sources
    const actualCost = parseFloat(fd?._actual_cost ?? fd?.actual_cost ?? 0);
    if (actualCost > 0) return Math.round(actualCost * 100);
    if (ps?.original_base_fare != null && parseFloat(ps.original_base_fare) > 0) {
      let base = parseFloat(ps.original_base_fare);
      if (hold > 0 && gt > 0) {
        const derivedBase = Math.max(0, gt - svc - hold);
        if (base > derivedBase + 1) base = derivedBase;
      }
      return Math.round(base * 100);
    }
    if (paymentMethod === 'defera_zero') {
      const gtVal = parseFloat(main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
      if (gtVal > 0) return Math.round(Math.max(0, gtVal - svc - hold) * 100);
    }
    const fdTotal = parseFloat(fd?.total_amount || fd?.totalAmount || 0);
    if (fdTotal > 0) return Math.round(fdTotal * 100);
    const mainTotal = parseFloat(main?.total_amount || main?.totalAmount || 0);
    const creditsTotal = parseFloat(fd?.credits_total || main?.credits_total || 0);
    const baseFromMain = creditsTotal > 0 ? mainTotal + creditsTotal : mainTotal;
    if (baseFromMain > 0) return Math.round(baseFromMain * 100);
    if (gt > 0 && svc >= 0) return Math.round(Math.max(0, gt - svc) * 100);
    return 0;
  }, [changeFlightTrip]);

  useEffect(() => {
    const fetchBookingOptions = async () => {
      // Hoisted ABOVE the try: the catch below reads bookingToken/searchParams
      // for incident tracking, and a const declared inside the try is invisible
      // there — the tracking line threw ReferenceError, so the toast + redirect
      // after it never ran and a failed fetch left the customer on the loader.
      // (selectedFlight was destructured here too but never used.)
      const { bookingToken, searchParams } = location.state || {};
      try {

        // If no bookingToken, we can't fetch options - redirect to search
        if (!bookingToken) {
          console.warn('⚠️ No bookingToken - user may have refreshed. Redirecting to search.');
          toast.error('Session expired. Please search again.');
          navigate('/search');
          return;
        }
        
        if (!searchParams) {
          toast.error('Missing search information');
          navigate('/search');
          return;
        }
        
        // The picked flight's search, so the search history chains these fares to it.
        const parentSearchId = (location.state?.returnFlight || location.state?.selectedFlight)?._search_id || null;
        const data = await getGoogleFlightsBookingOptions(bookingToken, searchParams, { parentSearchId });
        
        // Filter booking options based on searched cabin class
        const requestedCabinClass = searchParams?.cabin_class || 'economy';
        let filteredOptions = data.bookingOptions || [];
        
        // Define premium keywords to filter out when economy was requested
        const premiumKeywords = ['Premium Select', 'Delta One', 'Business', 'First Class', 'Premium Economy'];
        
        if (requestedCabinClass === 'economy') {
          // Filter out options with premium keywords in the title
          // Note: Options without option_title are typically the base economy/main cabin fare
          const economyOptions = filteredOptions.filter(option => {
            const mainOption = option.together || option.departing;
            const title = mainOption?.option_title || '';
            // If no title, assume it's economy/main cabin (include it)
            if (!title) return true;
            // Otherwise, filter out premium keywords
            return !premiumKeywords.some(keyword => title.includes(keyword));
          });
          
          // If we found economy options, use those. Otherwise, show warning but display all options
          if (economyOptions.length > 0) {
            filteredOptions = economyOptions;
          } else {
            // Show warning that only premium options are available
            toast.warning('Only premium cabin options are available for this flight. Try selecting a different flight for economy options.');
          }
        }
        
        // Budget/international carriers in Google's feed often expose a single
        // option with NO `option_title` (no fare context at all). For an
        // economy search that bare option is the airline's super-low base fare,
        // so stamp it with a real brand ("Basic Economy") here at the single
        // ingestion point. Doing it once means display, dedup, the user's
        // selection, and the brand persisted onto the booking all stay
        // consistent — instead of a contextless "Main Cabin" placeholder that
        // never reaches the trips dashboard. Premium searches get a brand only
        // with evidence on the option itself: United's Base Business arrived
        // untitled on TS2MFP and was sold as plain "Business" (see
        // unbrandedFareNameForOption / 2ZZMU6).
        filteredOptions = filteredOptions.map(option => withInferredFareNames(option, requestedCabinClass));

        // Separate-tickets options (Google pairs a cross-airline outbound +
        // return as two independent purchases) carry NO `together` — only
        // `departing` and `returning`, each priced for ITS leg alone. Every
        // consumer below falls back `together || departing`, which priced the
        // WHOLE round trip at the outbound leg's fare (LAX–JFK canary,
        // 2026-07-20: $194 shown for a $498 two-ticket trip — Defera eats the
        // difference at ticketing). Normalize at this same single ingestion
        // point: synthesize a combined `together` view (summed price, both
        // providers, merged fare metadata) so pricing/sorting/display are
        // honest. The original departing/returning legs stay on the option —
        // the persisted booking keeps both legs' prices and booking data for
        // ops, who ticket each separately. Pairs missing a price on either
        // leg are dropped: an unpriceable option must never sort to the top
        // as $0 or fall back to single-leg pricing.
        filteredOptions = filteredOptions
          .map(option => {
            if (option.together || !option.departing || !option.returning) return option;
            const dep = option.departing;
            const ret = option.returning;
            const depPrice = Number(dep.price) || 0;
            const retPrice = Number(ret.price) || 0;
            if (depPrice <= 0 || retPrice <= 0) return null;
            return {
              ...option,
              separate_tickets: true,
              together: {
                ...dep,
                is_separate_tickets: true,
                price: depPrice + retPrice,
                book_with: [dep.book_with, ret.book_with].filter(Boolean).join(' + '),
                marketed_as: [...(dep.marketed_as || []), ...(ret.marketed_as || [])],
                extensions: [...new Set([
                  ...(dep.extensions || []),
                  ...(ret.extensions || []),
                  'Booked as 2 separate tickets',
                ])],
                baggage_prices: [...new Set([...(dep.baggage_prices || []), ...(ret.baggage_prices || [])])],
                // No single purchase exists for the pair — per-leg booking
                // requests live on option.departing / option.returning.
                booking_request: null,
              },
            };
          })
          .filter(Boolean);

        setBookingOptions(filteredOptions);
        setSelectedFlights(data.selectedFlights);
        setBaggagePrices(data.baggagePrices);
        setPriceInsights(data.priceInsights);
      } catch (error) {
        console.error('Failed to fetch booking options:', error);
        console.error('Error details:', error?.message, error?.stack);
        // Server-side visibility for a page that otherwise fails invisibly:
        // during the 2026-07-28 empty-options incident the decisive clue
        // (which provider, which failure mode) only existed in customers'
        // browser consoles. Mark the ref so the render-side effect doesn't
        // double-report this token as an api_empty.
        optionsResultTrackedRef.current = bookingToken;
        trackEvent('booking_options_result', {
          origin: searchParams?.slices?.[0]?.origin || null,
          destination: searchParams?.slices?.[0]?.destination || null,
          token_kind: bookingTokenKind(bookingToken),
          outcome: 'fetch_error',
          error: String(error?.message || 'unknown').slice(0, 180),
        });
        // The raw error is in the telemetry above; the customer gets words.
        toast.error(fareOptionsFailureMessage(error));
        navigate('/search');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingOptions();
  }, [location.state, navigate]);

  // AI Price Forecast — once Google price insights are loaded, ask the backend
  // to assess (with AI) where this fare is likely to head before departure. Uses
  // the data we already have, so it adds no SerpAPI cost; fails silently.
  // Skip entirely when changing a flight: the customer is locked into the trip
  // they already booked and isn't shopping the market, so the forecast adds
  // noise and would burn an extra backend call per page load.
  useEffect(() => {
    if (isChangeFlight) { setPriceForecast(null); return; }
    if (!priceInsights) { setPriceForecast(null); return; }
    const sp = location.state?.searchParams || contextSearch || {};
    const slices = Array.isArray(sp.slices) ? sp.slices : [];
    const out = slices[0] || {};
    const ret = slices[1] || null;
    const paxCount = Array.isArray(sp.passengers) ? sp.passengers.length : 1;
    const airline = selectedFlights?.[0]?.flights?.[0]?.airline
      || selectedFlights?.[0]?.airline
      || null;
    if (!out.origin || !out.destination) { setPriceForecast(null); return; }

    let cancelled = false;
    (async () => {
      setPriceForecastLoading(true);
      try {
        const resp = await fetch('/api/price-forecast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin: out.origin,
            destination: out.destination,
            outbound_date: out.departure_date,
            return_date: ret?.departure_date || null,
            cabin_class: sp.cabin_class || 'economy',
            passenger_count: paxCount,
            marketing_airline: airline,
            price_insights: priceInsights,
          }),
        });
        if (!resp.ok) throw new Error('forecast failed');
        const data = await resp.json();
        if (!cancelled) setPriceForecast(data?.forecast || null);
      } catch {
        if (!cancelled) setPriceForecast(null);
      } finally {
        if (!cancelled) setPriceForecastLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [priceInsights, selectedFlights, location.state, contextSearch, isChangeFlight]);

  const handleSelectOption = (index) => {
    setSelectedOptionIndex(index);
    setSelectedOption(filteredDisplayOptions[index] ?? null);
  };

  const handleContinue = async () => {
    if (!selectedOption) {
      toast.error('Please select a booking option');
      return;
    }

    // Router state wins when present — do not merge with context (stale abandoned_trips).
    const { selectedFlight, returnFlight, searchParams, tripMode, packageType } =
      location.state != null
        ? {
            selectedFlight: location.state.selectedFlight ?? null,
            returnFlight: 'returnFlight' in location.state ? location.state.returnFlight : null,
            searchParams: location.state.searchParams ?? null,
            tripMode: location.state.tripMode ?? 'flight',
            packageType: location.state.packageType ?? 'flight'
          }
        : {
            selectedFlight: contextFlight,
            returnFlight: contextReturn,
            searchParams: contextSearch,
            tripMode: 'flight',
            packageType: 'flight'
          };
    
    // When refundable fare + Defera Zero card (not "Installments" Slice bypass): store `defera_zero`.
    // Defera Capital must NOT win here — it previously ran before the Zero branch and forced `defera_capital` into context, so Review showed Capital for Zero trips.
    const choseRefundableInstallments = selectedOption?._paymentVariant === 'installments';
    const refundableForDefera = isFullyRefundableOption(selectedOption);
    const isDeferaZeroContinuePath =
      !isChangeFlight &&
      !choseRefundableInstallments &&
      refundableForDefera &&
      (paymentMethod === 'installments' || paymentMethod === 'defera_zero');

    let effectivePaymentMethod;
    if (choseRefundableInstallments) {
      effectivePaymentMethod = 'installments';
    } else if (installmentsCheckoutProvider == null) {
      effectivePaymentMethod = paymentMethod;
    } else if (isDeferaZeroContinuePath) {
      effectivePaymentMethod = 'defera_zero';
    } else if (
      !isChangeFlight &&
      installmentsCheckoutProvider === 'defera_capital' &&
      (paymentMethod === 'installments' ||
        paymentMethod === 'miles_installments' ||
        paymentMethod === 'defera_capital')
    ) {
      effectivePaymentMethod = 'defera_capital';
    } else {
      effectivePaymentMethod = paymentMethod;
    }
    const bookingOptionForState = stampFare(choseRefundableInstallments && selectedOption
      ? (() => { const { _paymentVariant, ...rest } = selectedOption; return rest; })()
      : selectedOption);

    // Everything this page needs to re-enter itself from Review's "Change
    // booking option" button. Snapshotted from THIS page's entry state (raw
    // paymentMethod, original bookingToken) rather than re-derived from the
    // forward state on Review's side, because effectivePaymentMethod's
    // transforms below (installments → defera_capital / defera_zero) are
    // lossy — echoing them back would change which options render on return
    // (e.g. an installments search that auto-upgraded to Defera Zero via a
    // refundable fare would come back filtered to DZ-only options).
    const bookingOptionsReturn = location.state?.bookingToken
      ? {
          bookingToken: location.state.bookingToken,
          searchParams,
          selectedFlight,
          returnFlight,
          paymentMethod,
          deferaZero,
          milesBalance,
          reserveBalanceCents,
          reserveTripId,
          tripMode,
          packageType,
        }
      : null;

    // One new booking session (one cart) for this fare pick, started BEFORE
    // the state below is written so nothing lands in an older cart.
    const sessionId = startNewSession();
    
    // CRITICAL: Persist selected flight and booking option to Supabase AFTER validation
    // This ensures we only persist when user has actually selected an option.
    // The search goes too: it is the traveler count the fare was priced for,
    // and signing in from Review or coming back from a Reserve top-up
    // returns to Review with no navigation state, so without it the traveler
    // form opened for one person.
    updateBookingState({
      selectedFlight: selectedFlight,
      returnFlight: returnFlight,
      searchParams: searchParams,
      bookingOption: bookingOptionForState,
      paymentMethod: effectivePaymentMethod,
      choseRefundableInstallments: choseRefundableInstallments
    });
    
    console.log('✅ Persisting flight selection to Supabase:', {
      selectedFlight: selectedFlight?.id,
      bookingOption: bookingOptionForState?.together?.option_title || bookingOptionForState?.departing?.option_title,
      paymentMethod: effectivePaymentMethod
    });
    
    // Check if this is a Flights + Stays package
    if (packageType === 'flight_stay' || tripMode === 'flight_stay') {
      // Navigate to Stays page for hotel selection
      navigate('/stays', {
        state: {
          selectedFlight: selectedFlight,
          returnFlight: returnFlight,
          searchParams,
          passengers: searchParams?.passengers || [],
          bookingOption: bookingOptionForState,
          selectedFlights: selectedFlights,
          tripMode: 'flight_stay',
          packageType: 'flight_stay',
          isGoogleFlight: true,
          sessionId: sessionId,  // Pass session_id to Stays
          paymentMethod: effectivePaymentMethod,
          choseRefundableInstallments: choseRefundableInstallments,
          deferaZero: effectivePaymentMethod === 'defera_zero',
          milesBalance: milesBalance,
          reserveBalanceCents: reserveBalanceCents,
          reserveTripId: reserveTripId
        }
      });
    } else if (location.state?.reserveSwitchTrip) {
      // Reserve Switch - navigate to Reserve Switch Review for comparison
      navigate('/reserve/switch-review', {
        state: {
          selectedFlight: selectedFlight,
          returnFlight: returnFlight,
          searchParams,
          sessionId: sessionId,
          passengers: searchParams?.passengers || [],
          bookingOption: bookingOptionForState,
          selectedFlights: selectedFlights,
          isGoogleFlight: true,
          paymentMethod: 'reserve',
          reserveSwitchTrip: location.state.reserveSwitchTrip
        }
      });
    } else if (location.state?.changeFlightTrip) {
      // Change Flight - navigate to Change Flight Review for checkout
      navigate('/trips/change-flight/review', {
        state: {
          selectedFlight: selectedFlight,
          returnFlight: returnFlight,
          searchParams,
          sessionId: sessionId,
          passengers: searchParams?.passengers || [],
          bookingOption: bookingOptionForState,
          selectedFlights: selectedFlights,
          isGoogleFlight: true,
          changeFlightTrip: location.state.changeFlightTrip
        }
      });
    } else {
      // Regular flight-only booking - seat selection step first (it forwards
      // this exact state to Review, silently skipping itself when the offer
      // has no Duffel seat maps)
      navigate(createPageUrl('SelectSeats'), {
        state: {
          selectedFlight: selectedFlight,
          returnFlight: returnFlight,
          searchParams,
          sessionId: sessionId,  // Pass session_id to Review
          passengers: searchParams?.passengers || [],
          bookingOption: bookingOptionForState,
          selectedFlights: selectedFlights,
          isGoogleFlight: true,
          paymentMethod: effectivePaymentMethod,
          choseRefundableInstallments: choseRefundableInstallments,
          milesBalance: milesBalance,
          reserveBalanceCents: reserveBalanceCents,
          reserveTripId: reserveTripId,
          deferaZero: effectivePaymentMethod === 'defera_zero',
          // SelectSeats forwards its whole state to Review, which uses this
          // to offer "Change booking option" without losing the session.
          bookingOptionsReturn
        }
      });
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      const formatted = price.toFixed(2);
      return `$${formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
    return price;
  };

  // Generate synthetic price history if not provided by API (memoized with stable primitive dependencies)
  const syntheticPriceHistory = useMemo(() => {
    if (!priceInsights?.lowest_price || !priceInsights?.typical_price_range || priceInsights.typical_price_range.length !== 2) {
      return null;
    }
    
    const daysAgo = ['57 days ago', '50 days ago', '43 days ago', '36 days ago', '29 days ago', '22 days ago', '15 days ago', '8 days ago', '1 day ago'];
    const min = priceInsights.typical_price_range[0];
    const max = priceInsights.typical_price_range[1];
    const range = max - min;
    
    // Generate realistic price fluctuation with deterministic seed based on booking token
    const seed = priceInsights.lowest_price + min + max;
    
    return daysAgo.map((date, index) => {
      // Start lower, gradually increase with some variation
      const basePrice = min + (range * 0.3) + ((index / daysAgo.length) * range * 0.5);
      // Use deterministic variation based on seed and index
      const variation = ((Math.sin(seed + index) + 1) / 2 - 0.5) * range * 0.15;
      const price = Math.round(basePrice + variation);
      
      return {
        date,
        price: Math.max(min, Math.min(max, price))
      };
    });
  }, [
    priceInsights?.lowest_price,
    priceInsights?.typical_price_range?.[0],
    priceInsights?.typical_price_range?.[1]
  ]);

  // The flights the fare options are checked against: the itinerary the
  // customer picked, else Google's own description of the booking token's flights.
  const fareCheckSlices = useMemo(() => (
    flightForDisplay?.slices?.length ? flightForDisplay.slices : slicesFromSelectedFlights(selectedFlights)
  ), [flightForDisplay, selectedFlights]);

  // Blocklist: third-party OTAs we never want to show (e.g. business-class.com reselling airline inventory)
  const BLOCKED_PROVIDERS = ['business-class.com', 'easemytrip', 'ease my trip'];

  // Only the airline's own fares for THESE flights. Every ticket in a fare
  // (both, on separate tickets) must be sold by an airline flying it, never an
  // online travel agency, and the fare's flight numbers must be the flights
  // shown (checkFareOptionIntegrity). This replaced two holes that let
  // agency-sold legs through on ticketed trips (DS68IN "Ovago, American",
  // GBTWO3 "Frontier, eDreams"): trusting Google's `airline: true`, which a
  // combined separate-tickets option copies from its outbound leg, and
  // accepting any seller string that merely CONTAINED a carrier's name.
  // Separate-ticket fares stay when every ticket is the airline's own
  // (founder, 2026-10-28). Post-merger ticketing still passes: "Hawaiian"
  // sells Alaska's flights (the 2026-07-28 incident).
  const airlineBookingOptionsRaw = bookingOptions.filter(option => {
    const mainOption = option.together || option.departing;
    const bookWith = (mainOption?.book_with || '').toLowerCase();
    const optionTitle = (mainOption?.option_title || '').toLowerCase();

    // Exclude blocklisted providers (check book_with and option_title)
    const combined = `${bookWith} ${optionTitle}`;
    if (BLOCKED_PROVIDERS.some(blocked => combined.includes(blocked.toLowerCase()))) {
      return false;
    }

    // If no book_with field, exclude it
    if (!mainOption?.book_with) {
      return false;
    }

    const check = checkFareOptionIntegrity({ bookingOption: option, slices: fareCheckSlices, fresh: true });
    if (!check.ok) {
      console.log('Filtering out fare option:', mainOption.book_with, check.problems.map(p => p.code).join(','));
    }
    return check.ok;
  });

  // Helpers: detect fully refundable vs non-refundable from extensions and option title
  const getExtensionsText = (option) => {
    const mainOption = option?.together || option?.departing || option?.returning;
    const arr = mainOption?.extensions || [];
    return arr.map(e => {
      if (typeof e === 'string') return e.toLowerCase();
      if (e && typeof e === 'object' && (e.text || e.value || e.label)) return String(e.text || e.value || e.label).toLowerCase();
      return '';
    }).filter(Boolean).join(' ');
  };
  const getOptionTitle = (option) => {
    const mainOption = option?.together || option?.departing || option?.returning;
    return (mainOption?.option_title || '').toLowerCase();
  };
  
  // Helper: get cabin class tier (higher number = higher cabin class)
  // -1 = basic economy, 0 = economy, 1 = premium economy, 2 = business, 3 = first
  const getCabinTier = (optionTitleOrCabinClass) => {
    const text = (optionTitleOrCabinClass || '').toLowerCase();
    if (text.includes('first')) return 3;
    if (text.includes('business') || text.includes('delta one')) return 2;
    if (text.includes('premium') && (text.includes('economy') || text.includes('select'))) return 1;
    if (text.includes('basic')) return -1; // Basic Economy is lowest tier
    return 0; // economy, main cabin, etc.
  };
  
  /** Cabin tier of a whole booking option, for card ordering. */
  const tierOfOption = (option) => getCabinTier(getOptionTitle(option));

  // Get the searched cabin class tier
  const requestedCabinClass = searchParams?.cabin_class || 'economy';
  const requestedCabinTier = getCabinTier(requestedCabinClass);
  
  const isFullyRefundableOption = (option) => {
    const title = getOptionTitle(option);
    const exts = getExtensionsText(option);
    const combined = `${title} ${exts}`;
    if (combined.includes('non-refundable') || combined.includes('no refund') || combined.includes('no flex')) return false;
    // Fare titles that indicate refundable (American Main Select, Southwest Anytime/Business Select, common flex/refundable names)
    const refundableTitles = ['main select', 'refundable', 'flex', 'fully refundable', 'economy flex', 'refundable fare', 'anytime', 'business select'];
    if (refundableTitles.some(r => title.includes(r))) return true;
    if (!exts) return false;
    return exts.includes('refundable') || exts.includes('free cancellation') || exts.includes('full refund');
  };
  const isNonRefundableOption = (option) => {
    return !isFullyRefundableOption(option);
  };
  
  // Check if a refundable option's cabin is at or above the searched cabin class
  const isRefundableAtOrAboveSearchedCabin = (option) => {
    const title = getOptionTitle(option);
    const optionCabinTier = getCabinTier(title);
    return optionCabinTier >= requestedCabinTier;
  };

  // CRITICAL: Deduplicate fare options by fare class (option_title) - keep first occurrence
  const airlineBookingOptions = useMemo(() => {
    const fareClassMap = new Map();
    airlineBookingOptionsRaw.forEach(option => {
      const mainOption = option.together || option.departing;
      const fareClass = mainOption?.option_title || 'Main Cabin';
      if (!fareClassMap.has(fareClass)) {
        fareClassMap.set(fareClass, option);
      }
    });
    return Array.from(fareClassMap.values());
  }, [airlineBookingOptionsRaw]);

  // Refundable-upsell auto-hide is DISABLED APP-WIDE (was: hide Basic
  // when Refundable was within $160). The threshold caused a silent
  // price jump between the return-card price and the checkout Base
  // Fare that customers reasonably read as bait-and-switch. All fare
  // brands are now always visible on the fare-selection card list —
  // Refundable is still sorted first and still gets its Defera Zero
  // badge, so the upsell is offered but the customer explicitly picks.
  //
  // Defera Zero (full flow) and Defera Zero Toggle branches below keep
  // their refundable-only behavior — those are payment-method-driven,
  // not price-driven, and the customer opted into that flow up front.
  //
  // Miles-only skips refundable filter (no installments to worry about).
  // Refundable options must also be at or above the searched cabin class.
  const { displayOptions, showRefundableOnly, dzNoRefundable = false } = useMemo(() => {
    if (isChangeFlight) {
      // Customers may change to any fare/cabin (including an upgrade) at any
      // time. If the new fare costs more than Actual Cost we bill the difference
      // upfront; at or below Actual Cost the change is free. No cabin cap.
      const getPrice = (opt) => parseFloat((opt?.together || opt?.departing)?.price) || 0;
      const sorted = [...airlineBookingOptions].sort((a, b) => getPrice(a) - getPrice(b));
      return { displayOptions: sorted, showRefundableOnly: false };
    }
    if (paymentMethod === 'miles') {
      // Miles: sort base to premium (Basic Economy → Economy → Economy Plus → First)
      const sorted = [...airlineBookingOptions].sort((a, b) => {
        const aTier = getCabinTier(getOptionTitle(a));
        const bTier = getCabinTier(getOptionTitle(b));
        return aTier - bTier;
      });
      return { displayOptions: sorted, showRefundableOnly: false };
    }
    // Defera Zero (full flow): only refundable fares. Add refundable + installments variant so customer can bypass DZ.
    if (deferaZero) {
      const refundableOpts = airlineBookingOptions.filter(opt =>
        isFullyRefundableOption(opt) && isRefundableAtOrAboveSearchedCabin(opt)
      );
      if (refundableOpts.length === 0) {
        // No refundable fares on this flight, so Defera Zero can't book it.
        // This used to return [] — a bare "No airline booking options
        // available" card with no explanation, indistinguishable from a
        // backend failure (it triggered a customer report on 2026-07-28,
        // and detection makes it common: isFullyRefundableOption matches on
        // title/extension heuristics that miss e.g. AA after Main Select was
        // retired). Instead: explain (dzNoRefundable drives a notice card in
        // the render) and offer every fare as an Installments variant, the
        // same escape hatch the DZ-toggle branch already provides. The
        // variant flag routes Continue to effectivePaymentMethod
        // 'installments' and is stripped before checkout state.
        // Guarded on airlineBookingOptions.length: if there are NO options
        // at all this is not a refundability problem — fall through to the
        // generic empty card rather than a misleading notice.
        if (airlineBookingOptions.length > 0) {
          const sorted = [...airlineBookingOptions].sort((a, b) => {
            const aTier = getCabinTier(getOptionTitle(a));
            const bTier = getCabinTier(getOptionTitle(b));
            return aTier - bTier;
          });
          return {
            displayOptions: sorted.map((opt) => ({ ...opt, _paymentVariant: 'installments' })),
            showRefundableOnly: false,
            dzNoRefundable: true,
          };
        }
        return { displayOptions: [], showRefundableOnly: true };
      }
      const getPrice = (opt) => parseFloat((opt?.together || opt?.departing)?.price) || 0;
      const cheapest = refundableOpts.reduce((a, b) => (getPrice(a) <= getPrice(b) ? a : b));
      return { displayOptions: withDepositTwin([cheapest], cheapest, tierOfOption), showRefundableOnly: true };
    }
    // Defera Zero Toggle (Installments): show only refundable fares; if none, fallback to all. Add refundable + installments variant.
    if (deferaZeroToggle) {
      const refundableOpts = airlineBookingOptions.filter(opt =>
        isFullyRefundableOption(opt) && isRefundableAtOrAboveSearchedCabin(opt)
      );
      if (refundableOpts.length > 0) {
        // Every refundable fare also gets its deposit-plan card (founder, 2026-10-26).
        return { displayOptions: withDepositTwins(refundableOpts, refundableOpts, tierOfOption), showRefundableOnly: true };
      }
      return { displayOptions: airlineBookingOptions, showRefundableOnly: false };
    }
    // Only consider refundable options that are at or above the searched cabin class
    const refundableOptions = airlineBookingOptions.filter(opt => 
      isFullyRefundableOption(opt) && isRefundableAtOrAboveSearchedCabin(opt)
    );
    if (refundableOptions.length === 0) {
      // No refundable: still sort base to premium (Basic Economy → Economy → Economy Plus → First)
      const sorted = [...airlineBookingOptions].sort((a, b) => {
        const aTier = getCabinTier(getOptionTitle(a));
        const bTier = getCabinTier(getOptionTitle(b));
        return aTier - bTier;
      });
      return { displayOptions: sorted, showRefundableOnly: false };
    }

    // Refundable-upsell auto-hide is DISABLED APP-WIDE — the previous
    // "hide Basic when Refundable is within $160" behavior caused a
    // silent price jump between the return card ($117 Basic) and the
    // checkout Base Fare ($256 Refundable) that made customers feel
    // bait-and-switched. All fare brands are always shown, sorted with
    // refundable prioritized, so the upsell is still OFFERED (customer
    // sees Refundable first, with a Defera Zero badge) but never silent
    // — the customer explicitly picks their fare.
    // Sort: refundable first (upsell), then non-refundable from base to premium (Basic Economy → Economy → Economy Plus → First)
    const sorted = [...airlineBookingOptions].sort((a, b) => {
      const aRefundable = isFullyRefundableOption(a) && isRefundableAtOrAboveSearchedCabin(a);
      const bRefundable = isFullyRefundableOption(b) && isRefundableAtOrAboveSearchedCabin(b);
      if (aRefundable && !bRefundable) return -1;
      if (!aRefundable && bRefundable) return 1;
      const aTier = getCabinTier(getOptionTitle(a));
      const bTier = getCabinTier(getOptionTitle(b));
      return aTier - bTier; // ascending: base to premium
    });
    // Every refundable fare also gets its deposit-plan card, not just the
    // cheapest: otherwise a pricier refundable fare could only be paid on
    // Defera Zero, and vanished entirely under two months out, when the Zero
    // cards are hidden (founder, 2026-10-26).
    return { displayOptions: withDepositTwins(sorted, refundableOptions, tierOfOption), showRefundableOnly: false };
  }, [airlineBookingOptions, paymentMethod, requestedCabinTier, deferaZero, deferaZeroToggle, isChangeFlight]);

  // Hide Defera Zero card when departure is not 2+ months out (DZ parameter)
  const filteredDisplayOptions = useMemo(() => {
    if (departureDateEligibleForDeferaZero) return displayOptions;
    return displayOptions.filter((opt) => {
      const isDzCard = isFullyRefundableOption(opt) && isRefundableAtOrAboveSearchedCabin(opt) && !opt._paymentVariant;
      return !isDzCard; // Hide DZ card, keep installments variant and non-refundable
    });
  }, [displayOptions, departureDateEligibleForDeferaZero]);

  // Telemetry: one booking_options_result event per options load. The page
  // fails INVISIBLY without this — the API can 200 with options and the
  // customer can still see the empty card, with the reason living only in
  // their browser console (2026-07-28: the carrier filter dropped every
  // Alaska option because Google switched Alaska ticketing to book_with
  // "Hawaiian"; diagnosis took hours because no server-side signal
  // distinguished "cards rendered" from "empty card"). `empty_reason` names
  // the stage that zeroed the list, which is exactly the datum that was
  // missing. Priya's benchmark reads user_activity_log, so a spike in
  // empty/error outcomes surfaces in her sweep without further wiring.
  useEffect(() => {
    if (isLoading) return;
    const token = location.state?.bookingToken;
    if (!token || optionsResultTrackedRef.current === token) return;
    optionsResultTrackedRef.current = token;
    const sp = location.state?.searchParams;
    const apiCount = bookingOptions.length;
    const carrierCount = airlineBookingOptionsRaw.length;
    const displayed = filteredDisplayOptions.length;
    const empty_reason = displayed > 0 ? null
      : apiCount === 0 ? 'api_empty'
      : carrierCount === 0 ? 'carrier_filter'
      : 'display_filter';
    trackEvent('booking_options_result', {
      origin: sp?.slices?.[0]?.origin || null,
      destination: sp?.slices?.[0]?.destination || null,
      token_kind: bookingTokenKind(token),
      outcome: displayed > 0 ? 'rendered' : 'empty',
      api_options: apiCount,
      carrier_options: carrierCount,
      displayed,
      payment_method: paymentMethod || null,
      defera_zero: deferaZero === true,
      dz_no_refundable: dzNoRefundable === true,
      empty_reason,
    });
    // Ref-guarded to one send per token; list identities beyond the guard
    // deps are read at send time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, filteredDisplayOptions, dzNoRefundable]);

  // Keep selectedOptionIndex in sync when options change
  useEffect(() => {
    if (selectedOptionIndex !== null && selectedOptionIndex >= filteredDisplayOptions.length) {
      setSelectedOptionIndex(filteredDisplayOptions.length > 0 ? 0 : null);
      setSelectedOption(filteredDisplayOptions[0] ?? null);
    }
  }, [filteredDisplayOptions, selectedOptionIndex]);

  // Auto-select the single option when we're in showRefundableOnly mode
  useEffect(() => {
    if (showRefundableOnly && filteredDisplayOptions.length === 1 && selectedOptionIndex === null) {
      setSelectedOptionIndex(0);
      setSelectedOption(filteredDisplayOptions[0]);
    }
  }, [showRefundableOnly, filteredDisplayOptions, selectedOptionIndex]);

  // Defera Zero search funnel: auto-navigate with paymentMethod defera_zero ($0-down product). Independent of Installments→Capital setting.
  const deferaZeroNavigatedRef = useRef(false);
  useEffect(() => {
    if (!deferaZero || isLoading || deferaZeroNavigatedRef.current) return;
    if (filteredDisplayOptions.length === 0 && airlineBookingOptions.length > 0) {
      toast.error('No refundable fares available for this flight. Defera Zero requires fully refundable fares (e.g. American Main Select).');
      navigate(createPageUrl('Search'));
      return;
    }
    // Only auto-navigate when exactly 1 option (no DZ vs Installments choice)
    if (selectedOption && filteredDisplayOptions.length === 1 && !selectedOption._paymentVariant) {
      deferaZeroNavigatedRef.current = true;
      const st = location.state;
      const { selectedFlight, returnFlight, searchParams, tripMode, packageType } =
        st != null
          ? {
              selectedFlight: st.selectedFlight ?? null,
              returnFlight: 'returnFlight' in st ? st.returnFlight : null,
              searchParams: st.searchParams ?? null,
              tripMode: st.tripMode ?? 'flight',
              packageType: st.packageType ?? 'flight'
            }
          : {
              selectedFlight: contextFlight,
              returnFlight: contextReturn,
              searchParams: contextSearch,
              tripMode: 'flight',
              packageType: 'flight'
            };
      if (packageType === 'flight_stay' || tripMode === 'flight_stay') return;
      const sessionId = startNewSession();
      const fare = stampFare(selectedOption);
      updateBookingState({
        selectedFlight: selectedFlight,
        returnFlight: returnFlight,
        searchParams: searchParams,
        bookingOption: fare,
        paymentMethod: 'defera_zero'
      });
      navigate(createPageUrl('SelectSeats'), {
        state: {
          selectedFlight: selectedFlight,
          returnFlight: returnFlight,
          searchParams: searchParams,
          sessionId,
          passengers: searchParams?.passengers || [],
          bookingOption: fare,
          selectedFlights: selectedFlights,
          isGoogleFlight: true,
          paymentMethod: 'defera_zero',
          milesBalance: milesBalance,
          reserveBalanceCents: reserveBalanceCents,
          deferaZero: true
        }
      });
    }
  }, [
    deferaZero,
    isLoading,
    filteredDisplayOptions.length,
    airlineBookingOptions.length,
    selectedOption,
    location.state,
    contextFlight,
    contextReturn,
    contextSearch,
    selectedFlights,
    milesBalance,
    reserveBalanceCents,
    navigate,
    startNewSession,
    updateBookingState,
    stampFare
  ]);

  // Conversion card: only show if fully refundable option exists AND selected is non-refundable
  // Hide when showRefundableOnly (refundable is the only option—no upsell needed)
  // Only suggest refundable options at or above the searched cabin class
  const conversionCardData = useMemo(() => {
    if (showRefundableOnly) return null;
    // Only consider refundable options that are at or above the searched cabin class
    const refundableOptions = airlineBookingOptions.filter(opt => 
      isFullyRefundableOption(opt) && isRefundableAtOrAboveSearchedCabin(opt)
    );
    if (refundableOptions.length === 0) return null;
    if (!selectedOption || !isNonRefundableOption(selectedOption)) return null;
    if (paymentMethod === 'miles') return null; // No installments for miles-only

    const mainSelected = selectedOption?.together || selectedOption?.departing;
    const selectedPrice = parseFloat(mainSelected?.price) || 0;

    const refundableOption = refundableOptions.reduce((cheapest, opt) => {
      const main = opt?.together || opt?.departing;
      const p = parseFloat(main?.price) || 0;
      return p < (parseFloat(cheapest?.together?.price || cheapest?.departing?.price) || Infinity) ? opt : cheapest;
    });
    const refundableMain = refundableOption?.together || refundableOption?.departing;
    const refundablePrice = parseFloat(refundableMain?.price) || 0;

    const priceDiff = Math.max(0, refundablePrice - selectedPrice);
    if (priceDiff <= 0) return null;

    let leadDaysForConversion = NaN;
    if (selectedFlights?.length > 0 && selectedFlights[0]?.flights?.[0]?.departure_airport?.time) {
      try {
        const dep = new Date(selectedFlights[0].flights[0].departure_airport.time);
        const today = new Date();
        dep.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        leadDaysForConversion = Math.floor((dep.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      } catch {
        /* NaN */
      }
    }
    const conversionDepositPct = getDefaultInstallmentDepositPercentForLeadDays(leadDaysForConversion, {
      deferaCapital: installmentsCheckoutProvider === 'defera_capital',
    });
    let perInstallment = 0;
    let moreToday = 0;
    if (selectedFlights?.length > 0 && selectedFlights[0]?.flights?.[0]?.departure_airport?.time) {
      try {
        const departureDateTime = selectedFlights[0].flights[0].departure_airport.time;
        const calcTotalBeforeHold = (price) => {
          const serviceFeeRate = getServiceFeeRate(price, installmentsCheckoutProvider === 'defera_capital');
          const serviceFee = price * serviceFeeRate;
          return price + serviceFee;
        };
        const selectedUpfront = Math.round(calcTotalBeforeHold(selectedPrice) * (conversionDepositPct / 100));
        const refundableUpfront = Math.round(calcTotalBeforeHold(refundablePrice) * (conversionDepositPct / 100));
        moreToday = Math.max(0, refundableUpfront - selectedUpfront);

        const refundableTotalBeforeHold = calcTotalBeforeHold(refundablePrice);
        const refundableGrandTotal = refundableTotalBeforeHold * 1.16;
        const plan = calculateInstallmentPlan({
          quoteDate: new Date(),
          departureDate: new Date(departureDateTime),
          planTotal: refundableGrandTotal,
          maxWeeklyAmount: 350
        });
        const installmentsCount = plan.weekly?.installmentsCount ?? plan.weekly?.payments?.length ?? 1;
        perInstallment = installmentsCount > 0 ? Math.round(priceDiff / installmentsCount) : priceDiff;
      } catch (_) {
        perInstallment = Math.round(priceDiff / 8);
        moreToday = Math.round(priceDiff * 0.15);
      }
    } else {
      perInstallment = Math.round(priceDiff / 8);
      moreToday = Math.round(priceDiff * 0.15);
    }

    const refundableMainOption = refundableOption?.together || refundableOption?.departing;
    const bookWith = (refundableMainOption?.book_with || '').toLowerCase();
    const optionTitle = (refundableMainOption?.option_title || '').toLowerCase();
    const isAmericanMainSelect = bookWith.includes('american') && optionTitle.includes('main select');

    // For Reserve: show additional out-of-pocket (reserveExtra delta), not total price difference
    let reserveMoreOutOfPocket = null;
    if (paymentMethod === 'reserve' && reserveBalanceCents != null) {
      const selectedTotalCents = Math.round(selectedPrice * 1.11 * 100);
      const refundableTotalCents = Math.round(refundablePrice * 1.11 * 100);
      const reserveExtraSelected = Math.max(0, selectedTotalCents - reserveBalanceCents);
      const reserveExtraRefundable = Math.max(0, refundableTotalCents - reserveBalanceCents);
      reserveMoreOutOfPocket = Math.max(0, reserveExtraRefundable - reserveExtraSelected);
    }

    return { refundableOption, priceDiff, perInstallment, moreToday, isAmericanMainSelect, reserveMoreOutOfPocket };
  }, [airlineBookingOptions, selectedOption, paymentMethod, selectedFlights, showRefundableOnly, reserveBalanceCents, installmentsCheckoutProvider]);

  if (isLoading) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="booking-options-loading"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}>

          {/* Elegant background with dynamic glow */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Vibrant gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-950/30 via-black to-brand-950/30"></div>
            
            {/* Floating orbs - more visible */}
            <div className="orbs-container">
              {[...Array(4)].map((_, i) => (
                <div key={i} className={`floating-orb orb-${i + 1}`}></div>
              ))}
            </div>
            
            {/* Elegant rotating rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              {[...Array(3)].map((_, i) => (
                <div 
                  key={i} 
                  className="search-ring"
                  style={{ 
                    animationDelay: `${i * 0.6}s`,
                    width: `${(i + 1) * 120}px`,
                    height: `${(i + 1) * 120}px`
                  }}
                ></div>
              ))}
            </div>
          </div>

          {/* Center content */}
          <motion.div
            className="relative z-10 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}>
            
            {/* Icon container with vibrant glow */}
            <div className="w-28 h-28 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-500/30 to-primary/40 rounded-full backdrop-blur-md border border-brand-400/30 shadow-2xl shadow-brand-500/50"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="search-pulse"></div>
                <Plane className="w-14 h-14 text-brand-400 relative z-10 animate-bounce-subtle" style={{ transform: 'rotate(-45deg)' }} />
              </div>
            </div>
            
            <h3 className="text-xl font-light text-white mb-3 tracking-tight">Loading Booking Options</h3>
            <p className="text-white/70 max-w-md mx-auto text-sm font-normal leading-relaxed">
              Finding the best fares for you
            </p>
            
            {/* Progress indicator */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse"></div>
              <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </motion.div>

          <style>{`
            /* Floating orbs with vibrant gradient */
            .orbs-container { 
              position: absolute; 
              inset: 0; 
              pointer-events: none; 
            }
            .floating-orb {
              position: absolute;
              border-radius: 50%;
              background: radial-gradient(circle at 30% 30%, rgba(32, 66, 238, 0.4), rgba(21, 20, 210, 0.3), transparent);
              filter: blur(60px);
              animation: float-orb 6s ease-in-out infinite;
            }
            .orb-1 { width: 400px; height: 400px; left: 10%; top: 20%; animation-delay: 0s; }
            .orb-2 { width: 350px; height: 350px; right: 10%; top: 30%; animation-delay: -2s; }
            .orb-3 { width: 300px; height: 300px; left: 50%; bottom: 20%; animation-delay: -4s; }
            .orb-4 { width: 280px; height: 280px; right: 30%; bottom: 25%; animation-delay: -1.5s; }
            
            @keyframes float-orb {
              0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
              25% { transform: translate(-40px, -30px) scale(1.15); opacity: 0.7; }
              50% { transform: translate(30px, 40px) scale(0.9); opacity: 0.6; }
              75% { transform: translate(-20px, 20px) scale(1.1); opacity: 0.65; }
            }
            
            /* Elegant rotating rings */
            .search-ring {
              position: absolute;
              border-radius: 50%;
              border: 1px solid rgba(32, 66, 238, 0.3);
              animation: rotate-ring 4s linear infinite;
            }
            
            @keyframes rotate-ring {
              0% { 
                transform: rotate(0deg) scale(0.8);
                opacity: 0.3;
              }
              50% { 
                transform: rotate(180deg) scale(1);
                opacity: 0.6;
              }
              100% { 
                transform: rotate(360deg) scale(0.8);
                opacity: 0.3;
              }
            }
            
            /* Center icon glow */
            .search-pulse {
              position: absolute;
              inset: -20px;
              background: radial-gradient(circle, rgba(32, 66, 238, 0.5), transparent 60%);
              animation: search-pulse-glow 2s ease-in-out infinite;
              border-radius: 50%;
            }
            
            @keyframes search-pulse-glow {
              0%, 100% { 
                transform: scale(0.8); 
                opacity: 0.4; 
              }
              50% { 
                transform: scale(1.5); 
                opacity: 0.8; 
              }
            }
            
            /* Plane subtle bounce animation */
            @keyframes bounce-subtle {
              0%, 100% { transform: translateY(0px) rotate(-45deg); }
              50% { transform: translateY(-8px) rotate(-45deg); }
            }
            .animate-bounce-subtle {
              animation: bounce-subtle 2s ease-in-out infinite;
            }
          `}</style>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <>
      <MarketingTags googleAds contentsquare />
      <div className="min-h-[70vh] py-8 relative">
      {/* Royal blue ambient wash — same treatment as the Rewards page */}
      <RoyalAmbientBackground />
      <div className="max-w-5xl mx-auto px-6 md:px-8 relative z-10">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/search')}
            className="mb-6 text-white/60 hover:text-white hover:bg-white/10 font-normal"
          >
            ← Back to flights
          </Button>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-white tracking-tight">Select booking option</h2>
        </div>

        {flightForDisplay?.slices && flightForDisplay.slices.length > 0 ? (
          <div className={cn(REVIEW_SECTION_CARD, 'mb-6')}>
            <SectionBand
              icon={CheckCircle}
              title="Selected Flights"
            />
            <div className="px-2 md:px-4 py-4 md:py-6">
              <div className="space-y-4 md:space-y-6">
                {flightForDisplay.slices.map((slice, sliceIndex) => {
                  const firstSegment = slice?.segments?.[0];
                  const flightDate = firstSegment?.departing_at ? format(parseISO(firstSegment.departing_at), 'EEEE, MMMM d, yyyy') : '';
                  return (
                    <div key={sliceIndex} className="px-2 md:px-0">
                      <div className="mb-3 px-2 md:px-0">
                        <h3 className="text-base md:text-lg font-light tracking-tight text-white">
                          {sliceIndex === 0 ? 'Outbound Flight' : 'Return Flight'}
                        </h3>
                        {flightDate && (
                          <p className="text-sm text-white/60 font-normal mt-1">{flightDate}</p>
                        )}
                      </div>
                      <FlightOfferCard
                        offer={{
                          ...flightForDisplay,
                          slices: [slice],
                          flightData: {
                            ...(flightForDisplay.flightData || flightForDisplay.flight_data || {}),
                            slices: flightForDisplay.slices,
                          },
                        }}
                        isReturnJourney={sliceIndex > 0}
                        hidePrice={true}
                        gradientBottomColor="#0A1233"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : selectedFlights && selectedFlights.length > 0 && !cameFromSearchWithOffer && (
          <div className={cn(REVIEW_SECTION_CARD, 'mb-6')}>
            <SectionBand
              icon={Plane}
              title={`Selected Flight${selectedFlights.length > 1 ? 's' : ''}`}
            />
            <div className="px-6 py-5">
              {selectedFlights.map((flight, index) => (
                <div key={index} className="mb-2 last:mb-0">
                  {flight.flights?.map((leg, legIndex) => (
                    <div key={legIndex} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-4">
                        {leg.airline_logo && (
                          <img src={leg.airline_logo} alt={leg.airline} className="h-8 w-8" />
                        )}
                        <div>
                          <div className="font-normal text-base text-white">{leg.airline} {leg.flight_number}</div>
                          <div className="text-sm text-white/60 font-normal">
                            {leg.departure_airport?.id} → {leg.arrival_airport?.id}
                          </div>
                          <div className="text-sm text-white/60 font-normal">
                            {new Date(leg.departure_airport?.time).toLocaleString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {baggagePrices && baggagePrices.together && (
          <div className={cn(REVIEW_SECTION_CARD, 'mb-8')}>
            <SectionBand
              icon={Luggage}
              title="Baggage Policy"
            />
            <ul className="px-6 py-5 text-sm text-white/60 font-normal space-y-1">
              {baggagePrices.together.map((policy, index) => (
                <li key={index}>{policy}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-2xl font-light text-white tracking-tight">Select an option</h2>
            <AllInPriceToggle checked={showAllInPrices} onCheckedChange={setShowAllInPrices} />
          </div>

          {dzNoRefundable && (
            <Card className={cn(REVIEW_SECTION_CARD, 'p-5 mb-4')}>
              <p className="text-sm text-white/85 font-normal leading-relaxed">
                This flight doesn&rsquo;t offer refundable fares, which Defera Zero requires.
                You can continue with an Installments plan below &mdash; or go back and pick a
                flight with refundable fares to keep $0 down.
              </p>
            </Card>
          )}

          {filteredDisplayOptions.length === 0 ? (
            <Card className={cn(REVIEW_SECTION_CARD, 'p-6 text-center text-white/85 font-normal mb-0')}>
              No airline booking options available
            </Card>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
            {filteredDisplayOptions.map((option, index) => {
              const mainOption = option.together || option.departing;
              const isSelected = selectedOptionIndex === index;
              const isRefundableInstallmentsVariant = option._paymentVariant === 'installments';
              const isRefundable = isFullyRefundableOption(option) && isRefundableAtOrAboveSearchedCabin(option) && !isRefundableInstallmentsVariant;
              
              // Calculate display price breakdown for miles
              const priceBreakdown = calculateDisplayPrice(parseFloat(mainOption.price), paymentMethod, milesBalance);
              const milesOnlyDisplay = priceBreakdown.milesUsed 
                ? `+${priceBreakdown.milesUsed.toLocaleString()} miles`
                : null;
              
              // Calculate upfront payment - MUST match Slice Pay: deposit = selectedDeposit% of (amount * 1.12)
              // Amount = cashPortion + serviceFee + seatTotal (seatTotal=0 at flight options stage)
              // For change flight: due today = price diff * 1.11 when diff > 0, else $0
              let upfrontAmount = null;
              let changeFlightDueTodayCents = null;
              if (isChangeFlight && changeFlightTrip?.length) {
                const main = changeFlightTrip[0];
                const fd = main?.flightData || main?.flight_data || {};
                const isDeferaZero = fd._payment_method === 'defera_zero' || fd.payment_method === 'defera_zero';
                const isDeferaCapital = fd._payment_method === 'defera_capital' || fd.payment_method === 'defera_capital';
                const newBaseFareCents = Math.round(parseFloat(mainOption.price || 0) * 100);
                // Basis = what Defera ACTUALLY paid (Actual Cost) first, then the
                // originally-booked base as fallback. We NEVER surface a flight
                // credit: at or below basis the change is free ($0 today); above
                // basis we bill the difference upfront. Any credit owed back to
                // the customer is handled manually by the admin at booking time.
                const actualCostRaw = parseFloat(
                  fd._actual_cost ?? fd.actual_cost ??
                  main?.actual_cost ?? main?.actualCost ?? 0
                );
                const actualCostCents = actualCostRaw > 0 ? Math.round(actualCostRaw * 100) : 0;
                const basisCents = actualCostCents > 0 ? actualCostCents : changeFlightOriginalBaseCents;
                const priceDiffCents = newBaseFareCents - basisCents;
                // Bill the fare difference upfront whenever the new fare exceeds the
                // basis (Actual Cost, else original base) — for EVERY payment method,
                // including Defera Zero/Capital. At or below basis the change is free.
                // Markup (11% service fee on the difference) matches the change-flight
                // edge function so this preview equals the actual charge.
                void isDeferaZero; void isDeferaCapital;
                changeFlightDueTodayCents = priceDiffCents > 0 ? Math.round(priceDiffCents * 1.11) : 0;
              } else if (selectedFlights && selectedFlights.length > 0) {
                try {
                  const baseFare = parseFloat(mainOption.price);
                  let leadDays = NaN;
                  try {
                    const depT = selectedFlights[0]?.flights?.[0]?.departure_airport?.time;
                    if (depT) {
                      const dep = new Date(depT);
                      const today = new Date();
                      dep.setHours(0, 0, 0, 0);
                      today.setHours(0, 0, 0, 0);
                      leadDays = Math.floor((dep.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    }
                  } catch {
                    /* NaN */
                  }
                  const defaultDepositPct = getDefaultInstallmentDepositPercentForLeadDays(leadDays, {
                    deferaCapital: installmentsCheckoutProvider === 'defera_capital',
                  });
                  const seatTotal = 0; // No seat selection at flight options stage
                  const effectivePmForUpfront = isRefundableInstallmentsVariant ? 'installments' : paymentMethod;

                  if (effectivePmForUpfront === 'miles_installments') {
                    const milesValueInDollars = milesToDollars(milesBalance);
                    const cashPortion = Math.max(0, baseFare - milesValueInDollars);
                    if (cashPortion === 0) {
                      upfrontAmount = 0;
                    } else {
                      const serviceFeeRate = getServiceFeeRate(cashPortion, installmentsCheckoutProvider === 'defera_capital');
                      const serviceFee = cashPortion * serviceFeeRate;
                      const totalBeforeHoldFee = cashPortion + serviceFee + seatTotal;
                      // Miles+Installments: due today = deposit% of (cash portion + service fee), rounded to nearest dollar
                      upfrontAmount = Math.round(totalBeforeHoldFee * (defaultDepositPct / 100));
                    }
                  } else if (effectivePmForUpfront === 'installments') {
                    const serviceFeeRate = getServiceFeeRate(baseFare, installmentsCheckoutProvider === 'defera_capital');
                    const serviceFee = baseFare * serviceFeeRate;
                    const totalBeforeHoldFee = baseFare + serviceFee + seatTotal;
                    upfrontAmount = Math.round((totalBeforeHoldFee * (defaultDepositPct / 100)) * 100) / 100;
                  }
                } catch (error) {
                  console.error('Error calculating upfront payment:', error);
                }
              }

              let reserveExtra = null;
              if (paymentMethod === 'reserve' && selectedFlights?.length > 0) {
                const baseFare = parseFloat(mainOption.price);
                const reserveServiceFee = baseFare * 0.11;
                const totalNeededCents = Math.round((baseFare + reserveServiceFee) * 100);
                reserveExtra = Math.max(0, totalNeededCents - reserveBalanceCents);
              }

              return (
                <button
                  key={index}
                  type="button"
                  className={cn(
                    'p-5 cursor-pointer transition-all duration-300 rounded-3xl flex-shrink-0 w-[280px] md:w-[320px] snap-start text-left [touch-action:manipulation]',
                    isSelected ? BRAND_OPTION_CARD_SELECTED : BRAND_OPTION_CARD_UNSELECTED,
                  )}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelectOption(index); }}
                  onPointerDown={(e) => { e.stopPropagation(); handleSelectOption(index); }}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {mainOption.airline_logos && mainOption.airline_logos.length > 0 && (
                            <img
                              src={mainOption.airline_logos[0]}
                              alt={mainOption.book_with}
                              className="h-7 w-7"
                            />
                          )}
                          <div>
                            <div className="font-light text-lg text-white tracking-tight">{mainOption.book_with}</div>
                            <div className="text-sm text-white/85 font-normal">
                              {mainOption.option_title || 'Main Cabin'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {(() => {
                        // Separate extensions into included vs cancellation/change items
                        const cancellationKeywords = ['change', 'cancel', 'refund', 'ticket', 'no changes', 'non-refundable'];
                        const includedItems = (mainOption.extensions || []).filter(ext => 
                          !cancellationKeywords.some(keyword => ext.toLowerCase().includes(keyword))
                        );
                        const cancellationItems = (mainOption.extensions || []).filter(ext => 
                          cancellationKeywords.some(keyword => ext.toLowerCase().includes(keyword))
                        );
                        
                        const hasContent = includedItems.length > 0 || mainOption.baggage_prices?.length > 0 || cancellationItems.length > 0;
                        
                        if (!hasContent) return null;
                        
                        return (
                          <div className="space-y-4 mt-4 mb-2">
                            {includedItems.length > 0 && (
                              <div className="text-xs text-white/60 font-normal">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Check className="h-3.5 w-3.5 text-white/60" />
                                  <span className="font-normal text-white/80">Included:</span>
                                </div>
                                <ul className="ml-5 space-y-0.5">
                                  {includedItems.map((ext, extIndex) => (
                                    <li key={extIndex}>{ext}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {mainOption.baggage_prices && mainOption.baggage_prices.length > 0 && (
                              <div className="text-xs text-white/60 font-normal">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Luggage className="h-3.5 w-3.5 text-white/60" />
                                  <span className="font-normal text-white/80">Baggage:</span>
                                </div>
                                <ul className="ml-5 space-y-0.5">
                                  {mainOption.baggage_prices.map((bag, bagIndex) => (
                                    <li key={bagIndex}>{bag}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {cancellationItems.length > 0 && (
                              <div className="text-xs text-white/60 font-normal">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <RotateCcw className="h-3.5 w-3.5 text-white/60" />
                                  <span className="font-normal text-white/80">Cancellation:</span>
                                </div>
                                <ul className="ml-5 space-y-0.5">
                                  {cancellationItems.map((item, itemIndex) => {
                                    let display = item || '';
                                    const lower = display.toLowerCase();
                                    if (lower.includes('full refund')) display = display.replace(/\bfull refunds?\b/gi, '75% refundable');
                                    if (lower.includes('fully refundable')) display = display.replace(/\bfully refundable\b/gi, '75% refundable');
                                    return <li key={itemIndex}>{display}</li>;
                                  })}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {option.separate_tickets && (
                        <div className="mt-3 p-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white/60 font-normal">
                          <Info className="inline h-4 w-4 mr-1.5 text-white/60" />
                          Separate tickets
                        </div>
                      )}
                    </div>

                    {/* Price section - always visible at bottom of card */}
                    <div className="mt-4 pt-4 border-t border-white/10">
                      {paymentMethod === 'reserve' ? (
                        <div className="flex items-center justify-between">
                          <div>
                            {reserveExtra === 0 ? (
                              <>
                                <div className="text-lg font-medium text-emerald-400">$0+</div>
                                <div className="text-sm font-normal text-white/60">
                                  ${(parseFloat(mainOption.price) * 1.11).toLocaleString('en-US', { maximumFractionDigits: 0 })} total
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-lg font-medium text-white">+${Math.ceil(reserveExtra / 100).toLocaleString()}</div>
                                <div className="text-sm font-normal text-white/60">
                                  ${(parseFloat(mainOption.price) * 1.11).toLocaleString('en-US', { maximumFractionDigits: 0 })} total
                                </div>
                              </>
                            )}
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-1.5 text-white/80">
                              <Check className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                      ) : ((upfrontAmount !== null || isRefundable || isRefundableInstallmentsVariant || changeFlightDueTodayCents !== null) && paymentMethod !== 'miles' && paymentMethod !== 'reserve') || (paymentMethod === 'defera_zero' && !isRefundableInstallmentsVariant) ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-lg font-medium text-white">
                              ${changeFlightDueTodayCents !== null
                                ? Math.round(changeFlightDueTodayCents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })
                                : (isRefundable && !isChangeFlight ? 0 : Math.round(upfrontAmount || 0)).toLocaleString('en-US', { maximumFractionDigits: 0 })} today
                            </div>
                            {paymentMethod === 'miles_installments' && milesOnlyDisplay ? (
                              <div className="text-sm font-normal text-white/60">
                                {milesOnlyDisplay}
                              </div>
                            ) : isChangeFlight ? (
                              <div className="text-sm font-normal text-white/60">
                                {changeFlightDueTodayCents > 0 ? 'Fare difference' : 'No extra charge'}
                              </div>
                            ) : (
                              <div key={showAllInPrices ? 'allin' : 'fare'} className="text-sm font-normal text-white/60 transform-gpu">
                                {(() => {
                                  // "Total" must be what checkout bills (Review's
                                  // grandTotalIncludingSeats), not the raw fare:
                                  // Zero = fare + 11% service + 15% hold; financed
                                  // = fare + tiered service fee + 15% hold. Ceil so
                                  // the card never understates checkout.
                                  const tierBase = parseFloat(mainOption.price);
                                  if (!Number.isFinite(tierBase)) {
                                    return `${formatDisplayPrice(tierBase, paymentMethod, milesBalance)} fare`;
                                  }
                                  if (!showAllInPrices) {
                                    // Customer toggled all-in display off — show the raw
                                    // fare, labeled as such so it never reads as a total.
                                    return `$${Math.ceil(tierBase).toLocaleString('en-US')} fare`;
                                  }
                                  const capitalTier = installmentsCheckoutProvider === 'defera_capital';
                                  const tierHoldRate = capitalTier ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
                                  const allIn = (isRefundable || paymentMethod === 'defera_zero')
                                    ? tierBase * 1.11 * (1 + tierHoldRate)
                                    : (tierBase + tierBase * getServiceFeeRate(tierBase, capitalTier)) * (1 + tierHoldRate);
                                  return `$${Math.ceil(allIn).toLocaleString('en-US')} total`;
                                })()}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isRefundableInstallmentsVariant && !isChangeFlight && (
                              /* The twin was visually identical to the Defera Zero
                                 card beside it — same airline, same fare name, same
                                 inclusions — differing only in the "today" figure.
                                 Nothing on it said it was the deposit plan. */
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/20 bg-white/[0.06] text-white/85 text-[11px] font-medium">
                                <Clock className="w-2.5 h-2.5" />
                                Deposit plan
                              </span>
                            )}
                            {isRefundable && !isChangeFlight && (
                              <span className="relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full overflow-hidden shadow-[0_0_12px_-3px_rgba(185,28,28,0.25)]">
                                <span className="absolute inset-0 bg-gradient-to-r from-red-800/30 via-orange-600/35 to-orange-500/30" />
                                <span className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(251,146,60,0.08)_50%,rgba(185,28,28,0.06)_100%)]" />
                                <span className="absolute inset-[1px] rounded-full border border-red-900/30" />
                                <span className="relative flex items-center gap-1 text-white text-[11px] font-medium">
                                  <Zap className="w-2.5 h-2.5 drop-shadow-[0_0_4px_rgba(251,146,60,0.5)]" />
                                  Defera Zero
                                </span>
                              </span>
                            )}
                            {isSelected && (
                              <div className="flex items-center gap-1.5 text-white/80">
                                <Check className="h-5 w-5" />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="text-lg font-light text-white tracking-tight">
                            {formatDisplayPrice(parseFloat(mainOption.price), paymentMethod, milesBalance)}
                          </div>
                          {isSelected && (
                            <div className="flex items-center gap-1.5 text-white/80">
                              <Check className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            </div>
          )}

          {/* Refundability conversion card - only when fully refundable option exists and non-refundable selected (not for change flight) */}
          {conversionCardData && !isChangeFlight && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className={cn('mt-6 relative overflow-hidden', BRAND_NESTED_PANEL, 'shadow-[0_0_40px_-12px_rgba(10,18,51,0.25)]')}
            >
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(10, 18, 51,0.12),transparent)] pointer-events-none" />
              <div className="relative p-6 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-900/40 border border-brand-800/30 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-brand-400" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium text-white tracking-tight">
                          Non-refundable fare selected
                        </h3>
                        <p className="text-[13px] text-white/60 font-normal leading-relaxed max-w-xl">
                          {paymentMethod === 'reserve'
                            ? "Non-refundable fares don't qualify for cash refunds. If you cancel, the airline may offer travel credit instead—and you'll need to pay off your Reserve balance before any credits are released."
                            : "Non-refundable fares don't qualify for cash refunds. If you cancel, the airline may offer travel credit instead—and you'll need to pay off your installment plan before any credits are released."}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-white/90 font-medium pl-[52px]">
                      Protect your hard earned money and opt in for refund protection if things change.
                    </p>
                    {conversionCardData.isAmericanMainSelect && (
                      <p className="text-sm text-emerald-400 font-normal pl-[52px]">
                        You&apos;ll also receive a free Extra Legroom seat and priority boarding.
                      </p>
                    )}
                  </div>
                  <div className="lg:flex-shrink-0 flex flex-col items-end gap-4 pl-[52px] lg:pl-0">
                    <div className="flex flex-col items-end gap-1">
                      {paymentMethod === 'reserve' ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-semibold text-brand-400 tracking-tight">+${Math.ceil((conversionCardData.reserveMoreOutOfPocket ?? 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                          <span className="text-sm text-white/50 font-normal">more</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-semibold text-brand-400 tracking-tight">+${conversionCardData.perInstallment.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                            <span className="text-sm text-white/50 font-normal">more per installment</span>
                          </div>
                          {conversionCardData.moreToday > 0 && (
                            <div className="flex items-baseline gap-1 text-xs font-normal">
                              <span className="text-brand-400/90">+${conversionCardData.moreToday.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                              <span className="text-white/50">more today</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        const idx = filteredDisplayOptions.findIndex(o => {
                          const a = (o.together || o.departing);
                          const b = (conversionCardData.refundableOption.together || conversionCardData.refundableOption.departing);
                          return a?.option_title === b?.option_title && String(a?.price) === String(b?.price);
                        });
                        if (idx >= 0) {
                          setSelectedOptionIndex(idx);
                          setSelectedOption(filteredDisplayOptions[idx]);
                        } else {
                          setSelectedOption(conversionCardData.refundableOption);
                        }
                        toast.success('Upgraded to fully refundable fare');
                      }}
                      className={cn('w-full sm:w-auto mt-1 px-6 py-3 h-auto min-h-[44px] text-sm rounded-2xl', BRAND_PRIMARY_BTN_CLASS)}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2.5" />
                      Opt in for refund protection
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {!isChangeFlight && (priceForecastLoading || priceForecast) && (() => {
          const dir = priceForecast?.direction;
          // `direction` stays an honest read on the server (we calibrate against
          // it), but the customer-facing pill never advertises a fall: a
          // shopper told the fare is dropping waits, and the fare they came
          // back for is not held for them. The 'down' case therefore makes NO
          // directional claim in either direction — it does not say "will drop"
          // and it does not say "will hold" — it just points at today's fare.
          const tone = dir === 'up'
            ? { Icon: TrendingUp, label: 'Likely to rise', pill: 'bg-orange-500/15 border-orange-400/30 text-orange-200' }
            : dir === 'flat'
              ? { Icon: Minus, label: 'Likely to hold', pill: 'bg-white/10 border-white/15 text-white/80' }
              : dir === 'down'
                ? { Icon: Clock, label: "Today's fare", pill: 'bg-white/10 border-white/15 text-white/80' }
                : { Icon: TrendingUp, label: '', pill: '' };
          const ToneIcon = tone.Icon;
          return (
            <div className={cn(REVIEW_SECTION_CARD, 'mb-6')}>
              <SectionBand
                icon={ToneIcon}
                title="Price Forecast"
                subtitle="What today's fare looks like against this route's typical range"
              />
              <div className="px-6 py-5">
                {(!priceForecast && priceForecastLoading) ? (
                  <div className="flex items-center gap-2 text-white/60 text-sm font-normal">
                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    Assessing fare trajectory with AI…
                  </div>
                ) : priceForecast ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border', tone.pill)}>
                        <ToneIcon className="w-4 h-4 shrink-0" />
                        {tone.label}
                      </span>
                      {priceForecast.confidence != null && (
                        <span className="text-xs text-white/50 font-normal tabular-nums">
                          {Math.round(priceForecast.confidence * 100)}% confidence
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-brand-300/70 font-normal sm:ml-auto">
                        <Sparkles className="w-3 h-3 shrink-0" />
                        AI assessed
                      </span>
                    </div>
                    {priceForecast.headline && (
                      <p className="text-base text-white font-normal leading-snug">{priceForecast.headline}</p>
                    )}
                    {priceForecast.reasoning && (
                      <p className="text-sm text-white/60 font-normal leading-relaxed">{priceForecast.reasoning}</p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })()}

        {priceInsights && (
          <div className={cn(REVIEW_SECTION_CARD, 'mb-6')}>
            <SectionBand
              icon={Info}
              title="Price Insights"
              subtitle="How today's prices compare with the typical range"
            />
            <div className="px-6 py-5 space-y-4">
              {/* Price Status Header */}
              {priceInsights.price_level && (
                <div>
                  <p className="text-base text-white font-normal">
                    Prices are currently <span className={`font-semibold ${
                      priceInsights.price_level.toLowerCase() === 'low' ? 'text-emerald-400' :
                      priceInsights.price_level.toLowerCase() === 'typical' ? 'text-brand-400' :
                      'text-red-400'
                    }`}>{priceInsights.price_level.toLowerCase()}</span> for your search
                  </p>
                  {priceInsights.typical_price_range && priceInsights.typical_price_range.length === 2 && (
                    <p className="text-sm text-white/60 font-normal mt-1">
                      The least expensive flights for similar trips usually cost between {formatDisplayPrice(priceInsights.typical_price_range[0], paymentMethod, milesBalance)}–{formatDisplayPrice(priceInsights.typical_price_range[1], paymentMethod, milesBalance)}.
                    </p>
                  )}
                </div>
              )}

              {/* Price Range Indicator */}
              {priceInsights.lowest_price && priceInsights.typical_price_range && priceInsights.typical_price_range.length === 2 && (
                <div className="space-y-2">
                  <div className="relative h-2 rounded-full overflow-hidden bg-gradient-to-r from-green-500 via-orange-500 to-red-500">
                    {/* Current price marker */}
                    {(() => {
                      const min = priceInsights.typical_price_range[0];
                      const max = priceInsights.typical_price_range[1];
                      const current = priceInsights.lowest_price;
                      const position = Math.min(Math.max(((current - min) / (max - min)) * 100, 0), 100);
                      
                      return (
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white border-2 border-gray-900 rounded-full shadow-lg z-10"
                          style={{ left: `${position}%` }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                            <div className="bg-brand-800 text-white text-xs px-2 py-1 rounded font-medium">
                              {formatDisplayPrice(current, paymentMethod, milesBalance)} is {priceInsights.price_level.toLowerCase()}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  <div className="flex justify-between text-xs text-white/60 font-normal">
                    <span>{formatDisplayPrice(priceInsights.typical_price_range[0], paymentMethod, milesBalance)}</span>
                    <span>{formatDisplayPrice(priceInsights.typical_price_range[1], paymentMethod, milesBalance)}</span>
                  </div>
                </div>
              )}

              {/* Price History Chart */}
              {(() => {
                const historyData = priceInsights.price_history || syntheticPriceHistory;
                
                return historyData && historyData.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-normal text-white/80 tracking-tight">Price history for this search</h4>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={historyData}>
                          <defs>
                            <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#2A3868" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#2A3868" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: '#9ca3af', fontSize: 11 }}
                            stroke="#374151"
                          />
                          <YAxis 
                            tick={{ fill: '#9ca3af', fontSize: 11 }}
                            stroke="#374151"
                            tickFormatter={(value) => `$${value}`}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1f2937', 
                              border: '1px solid #374151',
                              borderRadius: '8px',
                              color: '#fff'
                            }}
                            formatter={(value) => [formatPrice(value), 'Price']}
                            labelStyle={{ color: '#9ca3af' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="price" 
                            stroke="#2A3868" 
                            strokeWidth={2}
                            fill="url(#priceGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 mb-12">
          <Button
            variant="outline"
            onClick={() => navigate('/search')}
            className="border-white/10 text-white bg-white/5 hover:bg-white/10 transition-all font-normal"
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedOption}
            className={cn('rounded-full font-normal disabled:opacity-50 disabled:cursor-not-allowed', BRAND_PRIMARY_BTN_CLASS)}
          >
            {(location.state?.packageType === 'flight_stay' || location.state?.tripMode === 'flight_stay') 
              ? 'Search for hotels' 
              : 'Continue to Review'}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
